'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { useRef } from 'react'
import { useEffect, useState } from 'react'

import { SquareArrowOutUpRight } from 'lucide-react'

import {
  LinkType,
  type ReeveButtonApiResponse,
} from '@/lib/api-connector-strapi-cms/api/reeve-landing-page/reeveLandingPageApi.types'
import type { ImageType } from '@/lib/models/components/image.types'

interface HeaderProps {
  logo: ImageType
  primaryButton: ReeveButtonApiResponse
  secondaryButton: ReeveButtonApiResponse
  apiAssetsUrl?: string
}

export const Header = ({
  logo,
  primaryButton,
  secondaryButton,
  apiAssetsUrl,
}: HeaderProps) => {
  const lastScrollRef = useRef(0)
  const [isScrollingUp, setIsScrollingUp] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = Math.max(
        0,
        window.pageYOffset || document.documentElement.scrollTop
      )

      setIsScrollingUp(scrollTop <= lastScrollRef.current)
      lastScrollRef.current = scrollTop
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div className="relative z-20">
      {/* Spacer div to prevent content jump when header becomes fixed */}
      <div className="h-[96px] w-full"></div>

      <section
        className={`nav-header-reeve w-full ${!isScrollingUp && 'scroll-up'} transition-all duration-500 ease-in-out`}
      >
        <div className="relative flex h-[96px] w-full items-center justify-center overflow-hidden border-y border-reeve-divider/50 bg-reeve-gray/50 px-4 backdrop-blur-[15px] xl:px-8">
          <div className="flex w-full max-w-[1440px] items-center justify-between">
            <Image
              className="h-8 w-auto"
              src={`${apiAssetsUrl || ''}${logo.url}`}
              width={logo.width}
              height={logo.height}
              alt={logo.alternativeText || 'Reeve logo'}
            />
            <div className="flex gap-x-4">
              <Link
                href={secondaryButton.link}
                target={
                  secondaryButton.type === LinkType.EXTERNAL
                    ? '_blank'
                    : undefined
                }
                className="group relative flex items-center justify-center overflow-hidden rounded-md bg-[#e8e4d8] px-4 py-1.5 text-[0.875rem] font-medium leading-[1.5rem] text-reeve-primary"
              >
                <span className="absolute inset-0 z-0 translate-y-full bg-gradient-to-t from-reeve-primary/[10%] to-[#e8e4d8] transition-transform duration-300 ease-out group-hover:translate-y-0"></span>
                <span className="relative z-10 flex items-center">
                  <div className="relative flex items-center">
                    {secondaryButton.label}
                    {secondaryButton.type === LinkType.EXTERNAL && (
                      <SquareArrowOutUpRight className="ml-1.5 size-4" />
                    )}
                    <span className="absolute bottom-[2px] left-0 h-[2px] w-0 bg-reeve-primary transition-all duration-300 group-hover:w-full"></span>
                  </div>
                </span>
              </Link>
              <Link
                href={primaryButton.link}
                target={
                  primaryButton.type === LinkType.EXTERNAL
                    ? '_blank'
                    : undefined
                }
                className="group relative flex items-center justify-center overflow-hidden rounded-md bg-reeve-primary px-4 py-1.5 text-[0.875rem] font-medium leading-[1.5rem] text-white"
              >
                <span className="absolute inset-0 z-0 translate-y-full bg-gradient-to-t from-white/[15%] to-reeve-primary transition-transform duration-300 ease-out group-hover:translate-y-0"></span>
                <span className="relative z-10 flex items-center">
                  <span className="relative flex items-center">
                    {primaryButton.label}
                    {primaryButton.type === LinkType.EXTERNAL && (
                      <SquareArrowOutUpRight className="ml-1.5 size-4" />
                    )}
                    <span className="absolute bottom-[2px] left-0 h-[2px] w-0 bg-white transition-all duration-300 group-hover:w-full"></span>
                  </span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
